(function () {
  'use strict';

  /**
   * Discounted price in cents for one unit. Ceil rounding to match Shopify automatic discounts.
   * Apply per unit, then multiply by quantity — Shopify rounds each unit then sums.
   */
  function discountedCents(unitCents, discountPercent) {
    if (!unitCents || unitCents <= 0) return 0;
    if (!discountPercent || discountPercent <= 0) return unitCents;
    const p = Math.max(0, Math.min(100, Math.round(discountPercent)));
    return Math.floor((unitCents * (100 - p) + 99) / 100);
  }

  function formatMoney(cents, moneyFormat) {
    if (cents == null || isNaN(cents)) return '—';
    const format = moneyFormat || '${{amount}}';
    const num = cents / 100;
    const value = num.toFixed(2);
    const intPart = Math.floor(num);
    const thousands = intPart.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const decPart = (Math.round((num - intPart) * 100)).toString().padStart(2, '0');
    const valueWithCommaSeparator = thousands + ',' + decPart;
    const rounded = Math.round(num).toString();
    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, valueWithCommaSeparator)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, thousands)
      .replace(/\{\{\s*amount\s*\}\}/g, value)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, rounded);
  }

  class BundleHeightsBuilder {
    constructor() {
      this.wrapper = document.querySelector('.bundle-heights-wrapper');
      if (!this.wrapper) return;

      this.config = window.bundleHeightsConfig || {};
      this.tiers = this.config.tiers || [
        { qty: 2, discount: 10 },
        { qty: 3, discount: 15 },
        { qty: 4, discount: 20 }
      ];
      const rawTexts = this.config.texts || {};
      // Decode HTML entities in all text values
      this.texts = {};
      for (const key in rawTexts) {
        if (typeof rawTexts[key] === 'string') {
          this.texts[key] = this.decodeHtmlEntities(rawTexts[key]);
        } else {
          this.texts[key] = rawTexts[key];
        }
      }
      this.moneyFormat = this.config.moneyFormat || '${{amount}}';

      this.state = {
        items: [],
        quantity: 0,
        currentTier: null,
        discount: 0,
        subtotal: 0,
        savings: 0,
        total: 0
      };

      this.drawerEl = null;
      this.drawerOverlay = null;
      this.drawerContent = null;
      this._processing = false;
      this.bundleId = null;

      this.init();
    }

    init() {
      this.drawerEl = document.getElementById('bundleProductDrawer');
      this.drawerOverlay = document.getElementById('bundleDrawerOverlay');
      this.drawerContent = document.getElementById('bundleDrawerContent');
      if (!this.drawerEl || !this.drawerContent) return;

      this.bindEvents();
      this.restoreFromStorage();
      this.updateUI();
    }

    getStorageKey() {
      const id = this.config.sectionId || this.wrapper?.dataset?.sectionId || 'default';
      return 'bundle-heights-' + id;
    }

    saveToStorage() {
      try {
        const key = this.getStorageKey();
        const data = { items: this.state.items };
        if (this.bundleId) data.bundleId = this.bundleId;
        localStorage.setItem(key, JSON.stringify(data));
      } catch (e) {
        /* ignore */
      }
    }

    restoreFromStorage() {
      try {
        const key = this.getStorageKey();
        const raw = localStorage.getItem(key);
        if (!raw) return;
        const data = JSON.parse(raw);
        if (!data || !Array.isArray(data.items) || data.items.length === 0) return;
        const items = data.items.filter(
          (i) => i && (i.variantId || i.variant_id) && Number(i.quantity) > 0
        );
        if (items.length === 0) return;
        this.state.items = items.map((i) => ({
          variantId: String(i.variantId || i.variant_id),
          quantity: Number(i.quantity) || 1,
          price: Number(i.price) || 0,
          title: i.title || '',
          variantTitle: i.variantTitle || i.variant_title || '',
          image: i.image || '',
          handle: i.handle || ''
        }));
        if (data.bundleId) this.bundleId = String(data.bundleId);
        this.recalculate();
      } catch (e) {
        /* ignore */
      }
    }

    bindEvents() {
      const w = this.wrapper;
      if (!w) return;

      w.addEventListener('click', (e) => {
        const addBtn = e.target.closest('[data-action="add-product"]');
        if (addBtn && !addBtn.disabled) {
          e.preventDefault();
          this.handleAddProduct(addBtn);
          return;
        }

        const removeBtn = e.target.closest('[data-action="remove-item"]');
        if (removeBtn) {
          e.preventDefault();
          this.handleRemoveItem(removeBtn);
          return;
        }

        const incBtn = e.target.closest('[data-action="increase-qty"]');
        if (incBtn) {
          e.preventDefault();
          this.handleIncreaseQty(incBtn);
          return;
        }

        const decBtn = e.target.closest('[data-action="decrease-qty"]');
        if (decBtn) {
          e.preventDefault();
          this.handleDecreaseQty(decBtn);
          return;
        }

        const detailsBtn = e.target.closest('[data-action="open-drawer"]');
        if (detailsBtn) {
          e.preventDefault();
          const handle = detailsBtn.dataset.productHandle;
          if (handle) this.openProductDrawer(handle);
          return;
        }
      });

      const checkoutBtn = document.getElementById('bundleCheckoutButton');
      if (checkoutBtn) {
        checkoutBtn.addEventListener('click', (e) => {
          e.preventDefault();
          if (!checkoutBtn.disabled) this.checkout();
        });
      }

      const summaryHeader = document.getElementById('bundleSummaryHeader');
      const summaryToggle = document.getElementById('bundleSummaryToggle');
      const summaryBody = document.getElementById('bundleSummaryBody');
      const toggleIcon = document.getElementById('bundleSummaryToggleIcon');
      const summaryBodyInner = summaryBody && summaryBody.querySelector('.bundle-summary-body-inner');
      const toggleAccordion = () => {
        if (!summaryBody || !summaryToggle) return;
        const expanded = summaryToggle.getAttribute('aria-expanded') === 'true';
        summaryToggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (expanded) {
          summaryBody.classList.add('is-collapsed');
          if (summaryBodyInner) summaryBodyInner.style.visibility = '';
        } else {
          summaryBody.classList.remove('is-collapsed');
          if (summaryBodyInner) summaryBodyInner.style.visibility = '';
        }
        if (toggleIcon) {
          toggleIcon.classList.remove(expanded ? 'fa-angle-up' : 'fa-angle-down');
          toggleIcon.classList.add(expanded ? 'fa-angle-down' : 'fa-angle-up');
        }
      };
      if (summaryBody && summaryBodyInner) {
        summaryBody.addEventListener('transitionend', (e) => {
          if (e.target === summaryBody && e.propertyName === 'grid-template-rows' && summaryBody.classList.contains('is-collapsed')) {
            summaryBodyInner.style.visibility = 'hidden';
          }
        });
      }
      if (summaryHeader && summaryBody) {
        summaryHeader.addEventListener('click', () => toggleAccordion());
      }
      if (summaryToggle) {
        summaryToggle.addEventListener('click', (e) => {
          e.stopPropagation();
          toggleAccordion();
        });
      }

      document.addEventListener('click', (e) => {
        const closeBtn = e.target.closest('[data-action="close-drawer"]');
        if (closeBtn) {
          e.preventDefault();
          this.closeProductDrawer();
          return;
        }

        const addFromDrawer = e.target.closest('[data-action="add-from-drawer"]');
        if (addFromDrawer) {
          e.preventDefault();
          this.handleAddFromDrawer(addFromDrawer);
          return;
        }
      });

      if (this.drawerOverlay) {
        this.drawerOverlay.addEventListener('click', () => this.closeProductDrawer());
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawerEl && this.drawerEl.classList.contains('active')) {
          this.closeProductDrawer();
        }
      });
    }

    handleAddProduct(button) {
      const variantId = button.dataset.variantId;
      const title = button.dataset.productTitle;
      const variantTitle = button.dataset.productVariantTitle || '';
      const price = parseInt(button.dataset.productPrice, 10);
      const image = button.dataset.productImage || '';
      const handle = button.dataset.productHandle || '';
      if (!variantId || !title || isNaN(price)) return;
      this.addProduct(variantId, price, title, image, variantTitle, handle);
    }

    handleRemoveItem(button) {
      const variantId = button.dataset.variantId;
      if (!variantId) return;
      this.removeProduct(variantId);
    }

    handleIncreaseQty(button) {
      const variantId = button.dataset.variantId;
      if (!variantId) return;
      this.changeQuantity(variantId, 1);
    }

    handleDecreaseQty(button) {
      const variantId = button.dataset.variantId;
      if (!variantId) return;
      this.changeQuantity(variantId, -1);
    }

    handleAddFromDrawer(button) {
      const variantId = button.dataset.variantId;
      const title = button.dataset.productTitle;
      const variantTitle = button.dataset.productVariantTitle || '';
      const price = parseInt(button.dataset.productPrice, 10);
      const image = button.dataset.productImage || '';
      const handle = button.dataset.productHandle || '';
      if (!variantId || !title || isNaN(price)) return;
      this.addProduct(variantId, price, title, image, variantTitle, handle);
      this.closeProductDrawer();
    }

    addProduct(variantId, price, title, image, variantTitle, handle) {
      const existing = this.state.items.find((i) => String(i.variantId) === String(variantId));
      if (existing) {
        existing.quantity += 1;
      } else {
        this.state.items.push({
          variantId,
          price,
          title,
          variantTitle: variantTitle || '',
          image,
          quantity: 1,
          handle: handle || ''
        });
      }
      this.recalculate();
      this.updateUI();
      this.saveToStorage();
    }

    changeQuantity(variantId, delta) {
      const item = this.state.items.find((i) => String(i.variantId) === String(variantId));
      if (!item) return;
      item.quantity = Math.max(0, item.quantity + delta);
      if (item.quantity <= 0) this.removeProduct(variantId);
      else {
        this.recalculate();
        this.updateUI();
        this.saveToStorage();
      }
    }

    removeProduct(variantId) {
      this.state.items = this.state.items.filter((i) => String(i.variantId) !== String(variantId));
      this.recalculate();
      this.updateUI();
      this.saveToStorage();
    }

    calculateTier() {
      const qty = this.state.quantity;
      let tier = 0;
      let discount = 0;
      let nextTier = null;

      for (let i = this.tiers.length - 1; i >= 0; i--) {
        if (qty >= this.tiers[i].qty) {
          tier = i + 1;
          discount = this.tiers[i].discount;
          break;
        }
        nextTier = { qty: this.tiers[i].qty, discount: this.tiers[i].discount };
      }

      return { tier, discount, nextTier };
    }

    recalculate() {
      let quantity = 0;
      let subtotal = 0;
      this.state.items.forEach((item) => {
        quantity += item.quantity;
        subtotal += item.price * item.quantity;
      });
      this.state.quantity = quantity;
      this.state.subtotal = subtotal;

      const { discount, nextTier } = this.calculateTier();
      this.state.currentTier = discount > 0 ? this.calculateTier().tier : null;
      this.state.discount = discount;

      let totalDiscounted = 0;
      this.state.items.forEach((item) => {
        const unitDisc = discountedCents(item.price, discount);
        totalDiscounted += unitDisc * item.quantity;
      });
      this.state.total = totalDiscounted;
      this.state.savings = subtotal - totalDiscounted;
    }

    updateUI() {
      this.updateTierBadges();
      this.updateProgress();
      this.updateSummary();
      this.updateProductCards();
      this.updateCheckoutButton();
    }

    updateTierBadges() {
      const badges = this.wrapper.querySelectorAll('.bundle-tier-badge');
      const tier = this.calculateTier().tier;
      badges.forEach((badge) => {
        const t = parseInt(badge.dataset.tier, 10);
        badge.classList.toggle('active', t === tier);
        badge.setAttribute('aria-pressed', t === tier ? 'true' : 'false');
      });
    }

    getTierPositionsAsPercent(block) {
      const bar = block && block.querySelector('.bundle-progress-bar');
      const tierEls = block ? block.querySelectorAll('.bundle-progress-tier') : [];
      if (!bar || tierEls.length === 0) return [33.33, 66.67, 100];
      const barRect = bar.getBoundingClientRect();
      if (barRect.width <= 0) return [33.33, 66.67, 100];
      const positions = [];
      tierEls.forEach((el) => {
        const r = el.getBoundingClientRect();
        const centerX = r.left + r.width / 2;
        const pct = ((centerX - barRect.left) / barRect.width) * 100;
        positions.push(Math.min(100, Math.max(0, pct)));
      });
      return positions.length >= 3 ? positions : [33.33, 66.67, 100];
    }

    updateProgress() {
      const template = this.texts.progressTemplate || 'Add {qty} more to save {discount}%';
      const { tier, discount, nextTier } = this.calculateTier();
      const qty = this.state.quantity;

      let headerText = '';
      const unlockedTemplate = this.texts.unlocked || "You've unlocked {discount}%";

      if (discount > 0) {
        headerText = unlockedTemplate.replace(/\{discount\}/g, String(discount));
      } else if (nextTier) {
        const need = Math.max(0, nextTier.qty - qty);
        headerText = template.replace(/\{qty\}/g, String(need)).replace(/\{discount\}/g, String(nextTier.discount));
      } else {
        headerText = unlockedTemplate.replace(/\{discount\}/g, String(this.tiers[this.tiers.length - 1]?.discount || 0));
      }

      const nextTierIndex = nextTier ? this.tiers.findIndex((t) => t.qty === nextTier.qty && t.discount === nextTier.discount) : -1;

      ['', 'Mobile'].forEach((suffix) => {
        const header = document.getElementById('bundleProgressHeader' + suffix);
        const fill = document.getElementById('bundleProgressFill' + suffix);
        const progressBlock = document.getElementById('bundleProgressBlock' + suffix);
        if (header) header.textContent = headerText;

        const tierPositions = progressBlock ? this.getTierPositionsAsPercent(progressBlock) : [33.33, 66.67, 100];
        const posTier1 = tierPositions[0];
        const posTier2 = tierPositions[1];
        const posTier3 = tierPositions[2];

        let width = 0;
        if (discount > 0) {
          if (tier === 1) width = posTier1;
          else if (tier === 2) width = posTier2;
          else width = 100;
        } else if (nextTier) {
          const segmentEnd = nextTierIndex === 0 ? posTier1 : nextTierIndex === 1 ? posTier2 : posTier3;
          width = nextTier.qty > 0 ? (qty / nextTier.qty) * segmentEnd : 0;
        } else {
          width = 100;
        }
        width = Math.min(100, Math.max(0, width));
        const widthPct = width + '%';

        if (fill) fill.style.width = widthPct;
        const bar = progressBlock && progressBlock.querySelector('.bundle-progress-bar');
        if (bar) {
          bar.setAttribute('aria-valuenow', Math.round(width));
          bar.setAttribute('aria-valuemax', 100);
        }
        const tiers = progressBlock ? progressBlock.querySelectorAll('.bundle-progress-tier') : [];
        tiers.forEach((el) => {
          const t = parseInt(el.dataset.tier, 10);
          el.classList.toggle('unlocked', t <= tier && tier > 0);
        });
      });

      const badge = document.getElementById('bundleSummaryBadge');
      if (badge) {
        const unlockedTemplate = this.texts.unlocked || "You've unlocked {discount}%";
        if (nextTier) {
          const need = Math.max(0, nextTier.qty - qty);
          const txt = template.replace(/\{qty\}/g, String(need)).replace(/\{discount\}/g, String(nextTier.discount));
          badge.textContent = txt;
          badge.classList.add('is-visible');
        } else if (discount > 0) {
          badge.textContent = unlockedTemplate.replace(/\{discount\}/g, String(discount));
          badge.classList.add('is-visible');
        } else {
          badge.textContent = '';
          badge.classList.remove('is-visible');
        }
      }
    }

    updateSummary() {
      const empty = document.getElementById('bundleSummaryEmpty');
      const list = document.getElementById('bundleSummaryList');
      const totals = document.getElementById('bundleSummaryTotals');
      const discRow = document.getElementById('bundleSummaryDiscountRow');
      const discVal = document.getElementById('bundleSummaryDiscount');
      const savRow = document.getElementById('bundleSummarySavingsRow');
      const savVal = document.getElementById('bundleSummarySavings');
      const hasItems = this.state.items.length > 0;
      const discount = this.state.discount;
      const savingsLabel = this.texts.savings || 'savings';

      if (empty) empty.style.display = hasItems ? 'none' : 'block';
      if (list) {
        list.style.display = hasItems ? 'block' : 'none';
        list.innerHTML = '';
        this.state.items.forEach((item) => {
          const originalLine = item.price * item.quantity;
          const unitDisc = discountedCents(item.price, discount);
          const discountedLine = unitDisc * item.quantity;
          const savingsLine = originalLine - discountedLine;
          const variantSub = item.variantTitle && item.variantTitle !== 'Default Title' ? '<span class="bundle-summary-item-variant">' + this.escapeHtml(item.variantTitle) + '</span>' : '';
          const origHtml = discount > 0 ? '<s class="bundle-summary-item-original">' + formatMoney(originalLine, this.moneyFormat) + '</s> ' : '';
          const discHtml = '<span class="bundle-summary-item-discounted">' + formatMoney(discountedLine, this.moneyFormat) + '</span>';
          const pillHtml = savingsLine > 0 ? '<span class="bundle-summary-item-pill">' + formatMoney(savingsLine, this.moneyFormat) + ' ' + this.escapeHtml(savingsLabel) + '</span>' : '';
          const removeLabel = this.texts.removeItem || 'Remove';
          const decreaseLabel = this.texts.decreaseQuantity || 'Decrease quantity';
          const increaseLabel = this.texts.increaseQuantity || 'Increase quantity';
          const li = document.createElement('li');
          li.className = 'bundle-summary-item';
          li.innerHTML = `
            <div class="bundle-summary-item-image">
              <img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.title)}" width="60" height="60" loading="lazy">
            </div>
            <div class="bundle-summary-item-details">
              <span class="bundle-summary-item-title">${this.escapeHtml(item.title)}</span>
              ${variantSub}
              <div class="bundle-summary-item-prices">
                ${origHtml}${discHtml}
                ${pillHtml}
              </div>
            </div>
            <div class="bundle-summary-item-qty">
              <button type="button" class="bundle-summary-btn bundle-summary-remove" data-action="remove-item" data-variant-id="${this.escapeHtml(String(item.variantId))}" aria-label="${this.escapeHtml(removeLabel)}">${this.trashIcon()}</button>
              <button type="button" class="bundle-summary-btn bundle-summary-minus" data-action="decrease-qty" data-variant-id="${this.escapeHtml(String(item.variantId))}" aria-label="${this.escapeHtml(decreaseLabel)}">−</button>
              <span class="bundle-summary-qty-num">${item.quantity}</span>
              <button type="button" class="bundle-summary-btn bundle-summary-plus" data-action="increase-qty" data-variant-id="${this.escapeHtml(String(item.variantId))}" aria-label="${this.escapeHtml(increaseLabel)}">+</button>
            </div>
          `;
          list.appendChild(li);
        });
      }

      if (totals) totals.style.display = hasItems ? 'block' : 'none';
      if (discRow) discRow.style.display = hasItems && discount > 0 ? 'flex' : 'none';
      if (discVal) discVal.innerHTML = discount > 0 ? discount + '%' : '—';
      if (savRow) savRow.style.display = hasItems ? 'flex' : 'none';
      if (savVal) savVal.innerHTML = formatMoney(this.state.savings, this.moneyFormat);
      const totalRow = document.getElementById('bundleSummaryTotalRow');
      const totalVal = document.getElementById('bundleSummaryTotal');
      if (totalRow) totalRow.style.display = hasItems ? 'flex' : 'none';
      if (totalVal) totalVal.innerHTML = hasItems ? formatMoney(this.state.total, this.moneyFormat) : '—';

      const countEl = document.getElementById('bundleSummaryCount');
      if (countEl) {
        const qty = this.state.quantity;
        const itemLabel = this.texts.item || 'item';
        const itemsLabel = this.texts.items || 'items';
        const emptyMsg = this.texts.countEmptyMessage || 'Count will be shown here for added products in bundle';
        countEl.textContent = qty > 0 ? (qty === 1 ? `1 ${itemLabel}` : `${qty} ${itemsLabel}`) : emptyMsg;
        countEl.classList.toggle('is-empty', qty === 0);
      }
    }

    trashIcon() {
      return '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>';
    }

    updateProductCards() {
      const discount = this.state.discount;
      const cards = this.wrapper.querySelectorAll('.bundle-product-card');
      cards.forEach((card) => {
        const variantId = card.dataset.variantId;
        const item = this.state.items.find((i) => String(i.variantId) === String(variantId));
        const addBlock = card.querySelector('[data-add-block]');
        const qtyBlock = card.querySelector('[data-qty-block]');
        const qtyNum = card.querySelector('[data-card-qty]');
        const badge = card.querySelector('[data-card-badge]');
        if (addBlock && qtyBlock) {
          if (item) {
            addBlock.style.display = 'none';
            qtyBlock.style.display = 'block';
            if (qtyNum) qtyNum.textContent = item.quantity;
            if (badge) {
              badge.textContent = item.quantity;
              badge.style.display = '';
            }
          } else {
            addBlock.style.display = '';
            qtyBlock.style.display = 'none';
            if (qtyNum) qtyNum.textContent = '0';
            if (badge) badge.style.display = 'none';
          }
        }

        const priceEl = card.querySelector('.bundle-product-price');
        const priceCents = parseInt(card.querySelector('[data-product-price]')?.dataset?.productPrice || card.querySelector('.bundle-product-price')?.dataset?.productPriceCents || '0', 10);
        if (priceEl && !isNaN(priceCents) && priceCents > 0) {
          if (discount > 0) {
            const discounted = discountedCents(priceCents, discount);
            priceEl.innerHTML = '<s class="bundle-product-price-original">' + formatMoney(priceCents, this.moneyFormat) + '</s> <span class="bundle-product-price-discounted">' + formatMoney(discounted, this.moneyFormat) + '</span>';
          } else {
            priceEl.innerHTML = '<span class="bundle-product-price-initial">' + formatMoney(priceCents, this.moneyFormat) + '</span>';
          }
        }
      });
    }

    updateCheckoutButton() {
      const btn = document.getElementById('bundleCheckoutButton');
      if (!btn) return;
      const disabled = this.state.items.length === 0;
      btn.disabled = disabled;
      btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }

    escapeHtml(s) {
      if (s == null) return '';
      const div = document.createElement('div');
      div.textContent = s;
      return div.innerHTML;
    }

    decodeHtmlEntities(s) {
      if (s == null) return '';
      // Handle HTML entities like &#39; (apostrophe), &amp; (ampersand), etc.
      const textarea = document.createElement('textarea');
      textarea.innerHTML = s;
      let decoded = textarea.value;
      // Also handle numeric entities that might not be caught
      decoded = decoded.replace(/&#(\d+);/g, (match, dec) => String.fromCharCode(dec));
      decoded = decoded.replace(/&#x([a-f\d]+);/gi, (match, hex) => String.fromCharCode(parseInt(hex, 16)));
      return decoded;
    }

    openProductDrawer(productHandle) {
      if (!this.drawerEl || !this.drawerContent) return;
      const loadingText = this.texts.loading || 'Loading…';
      this.drawerContent.innerHTML = '<div class="bundle-drawer-loading">' + this.escapeHtml(loadingText) + '</div>';
      this.drawerEl.classList.add('active');
      this.drawerEl.setAttribute('aria-hidden', 'false');
      document.body.classList.add('bundle-drawer-open');

      this.fetchProductDetails(productHandle)
        .then((product) => {
          this.renderProductDrawer(product);
        })
        .catch(() => {
          const errorText = this.texts.unableToLoad || 'Unable to load product.';
          this.drawerContent.innerHTML = '<div class="bundle-drawer-error">' + this.escapeHtml(errorText) + '</div>';
        });
    }

    closeProductDrawer() {
      if (!this.drawerEl) return;
      this.drawerEl.classList.remove('active');
      this.drawerEl.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('bundle-drawer-open');
      
      // Clean up keyboard listener
      if (this._drawerKeyHandler) {
        document.removeEventListener('keydown', this._drawerKeyHandler);
        this._drawerKeyHandler = null;
      }
      
      setTimeout(() => {
        if (this.drawerContent) this.drawerContent.innerHTML = '';
      }, 300);
    }

    async fetchProductDetails(productHandle) {
      const res = await fetch(`/products/${productHandle}.js`);
      if (!res.ok) throw new Error('Product not found');
      return res.json();
    }

    renderProductDrawer(product) {
      if (!product || !this.drawerContent) return;
      const v = product.variants && product.variants[0];
      if (!v) return;

      // Check for custom bundle image in metafields, fallback to featured image
      let img = product.featured_image || (product.images && product.images[0]) || '';
      if (product.metafields && product.metafields.custom && product.metafields.custom.bundle_image) {
        img = product.metafields.custom.bundle_image;
      }
      
      const images = product.images || [];
      const hasMultipleImages = images.length > 1;
      const indicators = this.renderDrawerIndicators(images);
      const arrows = hasMultipleImages ? this.renderDrawerArrows() : '';
      const counter = hasMultipleImages ? `<div class="bundle-drawer-image-counter" id="drawerImageCounter">1 / ${images.length}</div>` : '';
      const benefits = this.renderDrawerBenefits(product);
      const ingredients = this.renderDrawerIngredients(product);
      const drawerAdd = this.texts.drawerAddButton || 'Add to Bundle';

      const closeText = this.texts.close || 'Close';
      const starsHtml = `
        <div class="bundle-drawer-stars" aria-label="5 out of 5 stars">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="#FFB800" stroke="#FFB800" stroke-width="1" class="bundle-star"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="#FFB800" stroke="#FFB800" stroke-width="1" class="bundle-star"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="#FFB800" stroke="#FFB800" stroke-width="1" class="bundle-star"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="#FFB800" stroke="#FFB800" stroke-width="1" class="bundle-star"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="#FFB800" stroke="#FFB800" stroke-width="1" class="bundle-star"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
          <span class="bundle-drawer-rating-text">(5.0)</span>
        </div>
      `;
      const html = `
        <div class="bundle-drawer-wrapper">
          <div class="bundle-drawer-header">
            <h2 class="bundle-drawer-subtitle">${this.escapeHtml(this.texts.freeShippingAlways || 'Free shipping always')}</h2>
            <button type="button" class="bundle-drawer-close" data-action="close-drawer" aria-label="${this.escapeHtml(closeText)}">×</button>
          </div>
          <div class="bundle-drawer-body">
            <div class="bundle-drawer-images" data-images='${this.escapeHtml(JSON.stringify(images))}'>
              <div class="bundle-drawer-image-container">
                ${arrows}
                <img src="${this.ensureProtocol(img)}" alt="${this.escapeHtml(product.title)}" class="bundle-drawer-main-image" id="drawerMainImage" width="600" height="600" data-current-index="0">
                ${counter}
              </div>
              ${indicators}
            </div>
            ${starsHtml}
            <h1 class="bundle-drawer-title">${this.escapeHtml(product.title)}</h1>
            <div class="bundle-drawer-price">${formatMoney(v.price, this.moneyFormat)}</div>
            <div class="bundle-drawer-description">${product.description || ''}</div>
            ${benefits}
            ${ingredients}
          </div>
          <div class="bundle-drawer-actions">
            <button type="button" class="bundle-drawer-add-button" data-action="add-from-drawer"
              data-variant-id="${v.id}"
              data-product-handle="${this.escapeHtml(product.handle || '')}"
              data-product-title="${this.escapeHtml(product.title)}"
              data-product-variant-title="${this.escapeHtml((v.title && v.title !== 'Default Title') ? v.title : '')}"
              data-product-price="${v.price}"
              data-product-image="${this.ensureProtocol(img)}">
              ${this.escapeHtml(drawerAdd)}
            </button>
            <span class="bundle-drawer-actions-text">${this.escapeHtml(this.texts.drawerSupplyText || '1x capsule - 30 day supply')}</span>

          </div>
        </div>
      `;

      this.drawerContent.innerHTML = html;
      this.bindDrawerIndicators();
      this.bindDrawerArrows();
      this.bindDrawerKeyboard();
      this.bindDrawerSwipe();
    }

    ensureProtocol(url) {
      if (!url) return '';
      return url.startsWith('//') ? 'https:' + url : url;
    }

    bindDrawerIndicators() {
      const main = this.drawerContent.querySelector('#drawerMainImage');
      const imagesEl = this.drawerContent.querySelector('.bundle-drawer-images');
      const indicators = this.drawerContent.querySelectorAll('.bundle-drawer-indicator');
      let images = [];
      try {
        const raw = imagesEl && imagesEl.getAttribute('data-images');
        if (raw) images = JSON.parse(raw);
      } catch (_) {}
      indicators.forEach((ind, index) => {
        ind.addEventListener('click', () => {
          let src = ind.dataset.fullSrc || ind.getAttribute('data-full-src');
          if (!src && images[index]) {
            const img = images[index];
            src = typeof img === 'string' ? img : img.src;
          }
          if (src && main) {
            this.changeDrawerImage(main, src, index);
          }
          indicators.forEach((x) => {
            x.classList.remove('active');
            x.setAttribute('aria-selected', 'false');
          });
          ind.classList.add('active');
          ind.setAttribute('aria-selected', 'true');
          const counter = this.drawerContent.querySelector('#drawerImageCounter');
          if (counter) counter.textContent = `${index + 1} / ${indicators.length}`;
        });
      });
    }

    bindDrawerArrows() {
      const leftArrow = this.drawerContent.querySelector('.bundle-drawer-arrow-left');
      const rightArrow = this.drawerContent.querySelector('.bundle-drawer-arrow-right');
      
      if (leftArrow) {
        leftArrow.addEventListener('click', () => this.navigateDrawerImage(-1));
      }
      if (rightArrow) {
        rightArrow.addEventListener('click', () => this.navigateDrawerImage(1));
      }
    }

    bindDrawerKeyboard() {
      const drawerEl = this.drawerEl;
      if (!drawerEl) return;
      
      const handleKeydown = (e) => {
        if (!drawerEl.classList.contains('active')) return;
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.navigateDrawerImage(-1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.navigateDrawerImage(1);
        }
      };
      
      // Remove old listener if exists
      if (this._drawerKeyHandler) {
        document.removeEventListener('keydown', this._drawerKeyHandler);
      }
      this._drawerKeyHandler = handleKeydown;
      document.addEventListener('keydown', handleKeydown);
    }

    bindDrawerSwipe() {
      const imageContainer = this.drawerContent.querySelector('.bundle-drawer-image-container');
      if (!imageContainer) return;
      
      let touchStartX = 0;
      let touchEndX = 0;
      const minSwipeDistance = 50;
      
      const handleTouchStart = (e) => {
        touchStartX = e.changedTouches[0].screenX;
      };
      
      const handleTouchEnd = (e) => {
        touchEndX = e.changedTouches[0].screenX;
        handleSwipe();
      };
      
      const handleSwipe = () => {
        const swipeDistance = touchStartX - touchEndX;
        
        if (Math.abs(swipeDistance) > minSwipeDistance) {
          if (swipeDistance > 0) {
            // Swiped left, go to next image
            this.navigateDrawerImage(1);
          } else {
            // Swiped right, go to previous image
            this.navigateDrawerImage(-1);
          }
        }
      };
      
      imageContainer.addEventListener('touchstart', handleTouchStart, { passive: true });
      imageContainer.addEventListener('touchend', handleTouchEnd, { passive: true });
    }

    navigateDrawerImage(direction) {
      const main = this.drawerContent.querySelector('#drawerMainImage');
      const imagesContainer = this.drawerContent.querySelector('.bundle-drawer-images');
      if (!main || !imagesContainer) return;
      
      const imagesData = imagesContainer.dataset.images;
      if (!imagesData) return;
      
      let images = [];
      try {
        images = JSON.parse(imagesData);
      } catch (e) {
        return;
      }
      
      if (images.length <= 1) return;
      
      const currentIndex = parseInt(main.dataset.currentIndex) || 0;
      let newIndex = currentIndex + direction;
      
      // Loop around
      if (newIndex < 0) newIndex = images.length - 1;
      if (newIndex >= images.length) newIndex = 0;
      
      const newImageSrc = typeof images[newIndex] === 'string' ? images[newIndex] : images[newIndex].src;
      this.changeDrawerImage(main, newImageSrc, newIndex);
      
      // Update active indicator
      const indicators = this.drawerContent.querySelectorAll('.bundle-drawer-indicator');
      indicators.forEach((ind, i) => {
        ind.classList.toggle('active', i === newIndex);
        ind.setAttribute('aria-selected', i === newIndex ? 'true' : 'false');
      });
      
      // Update counter
      const counter = this.drawerContent.querySelector('#drawerImageCounter');
      if (counter) {
        counter.textContent = `${newIndex + 1} / ${images.length}`;
      }
    }

    changeDrawerImage(imgElement, src, index) {
      if (!imgElement) return;
      
      // Add fade effect
      imgElement.classList.add('fading');
      
      setTimeout(() => {
        imgElement.src = this.ensureProtocol(src);
        imgElement.dataset.currentIndex = index;
        imgElement.classList.remove('fading');
      }, 150);
    }

    renderDrawerArrows() {
      const prevText = this.texts.previousImage || 'Previous image';
      const nextText = this.texts.nextImage || 'Next image';
    return `
        <button type="button" class="bundle-drawer-arrow bundle-drawer-arrow-left" aria-label="${this.escapeHtml(prevText)}">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <button type="button" class="bundle-drawer-arrow bundle-drawer-arrow-right" aria-label="${this.escapeHtml(nextText)}">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 18L15 12L9 6" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      `;
    }

    renderDrawerIndicators(images) {
      if (!images || images.length <= 1) return '';
      const slideIndicatorsLabel = this.texts.slideIndicators || 'Slide indicators';
      const slideOfTemplate = this.texts.slideOfTemplate || 'Slide {{ current }} of {{ total }}';
      const list = images.map((img, i) => {
        const u = this.ensureProtocol(typeof img === 'string' ? img : img.src);
        const slideLabel = slideOfTemplate.replace(/\{\{\s*current\s*\}\}/g, String(i + 1)).replace(/\{\{\s*total\s*\}\}/g, String(images.length));
        return `<button type="button" class="bundle-drawer-indicator ${i === 0 ? 'active' : ''}" data-index="${i}" data-full-src="${u}" aria-label="${this.escapeHtml(slideLabel)}" aria-selected="${i === 0 ? 'true' : 'false'}"></button>`;
      }).join('');
      return `<div class="bundle-drawer-indicators" role="tablist" aria-label="${this.escapeHtml(slideIndicatorsLabel)}">${list}</div>`;
    }

    renderDrawerBenefits(product) {
      const raw = product.metafields && (product.metafields.custom?.benefits || product.metafields.benefits);
      let benefits = [];
      if (typeof raw === 'string') benefits = raw.split('|').map((b) => b.trim()).filter(Boolean);
      else if (Array.isArray(raw)) benefits = raw;
      if (benefits.length === 0) return '';
      const benefitsLabel = this.texts.benefits || 'Benefits';
      const list = benefits.map((b) => `<li>${this.escapeHtml(b)}</li>`).join('');
      return `<div class="bundle-drawer-benefits"><h3>${this.escapeHtml(benefitsLabel)}</h3><ul>${list}</ul></div>`;
    }

    renderDrawerIngredients(product) {
      const ing = product.metafields && (product.metafields.custom?.ingredients || product.metafields.ingredients);
      if (!ing) return '';
      const ingredientsLabel = this.texts.ingredients || 'Ingredients';
      const content = typeof ing === 'string' ? this.escapeHtml(ing) : (ing.value || '');
      return `<div class="bundle-drawer-ingredients"><h3>${this.escapeHtml(ingredientsLabel)}</h3><div class="bundle-drawer-ingredients-content">${content}</div></div>`;
    }

    generateBundleId() {
      return 'bundle-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
    }

    showCartDrawerLoading(cartDrawer) {
      const inner = cartDrawer && cartDrawer.querySelector('.drawer__inner');
      if (!inner) return null;
      const el = document.createElement('div');
      el.className = 'bundle-cart-drawer-loading';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.id = 'bundleCartDrawerLoading';
      const updatingText = this.texts.updatingCart || 'Updating cart…';
      el.innerHTML = '<div class="bundle-cart-drawer-spinner" aria-hidden="true"></div><span class="bundle-cart-drawer-loading-text">' + this.escapeHtml(updatingText) + '</span>';
      inner.appendChild(el);
      return el;
    }

    hideCartDrawerLoading() {
      const el = document.getElementById('bundleCartDrawerLoading');
      if (el && el.parentNode) el.parentNode.removeChild(el);
    }

    async refreshCartDrawerAndOpen(cartDrawer) {
      try {
        const cartUrl = (typeof window.routes !== 'undefined' && window.routes.cart_url) ? window.routes.cart_url : '/cart';
        const ts = Date.now();
        const drawerRes = await fetch(`${cartUrl}?section_id=cart-drawer&_=${ts}`, { cache: 'no-store' });
        if (!drawerRes.ok) return false;
        const html = await drawerRes.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newItems = doc.querySelector('cart-drawer-items');
        const newFooter = doc.querySelector('.cart-drawer__footer');
        const curItems = document.querySelector('cart-drawer-items');
        const curFooter = document.querySelector('.cart-drawer__footer');
        if (newItems && curItems) curItems.replaceWith(newItems);
        if (newFooter && curFooter) curFooter.replaceWith(newFooter);
        const isEmpty = doc.querySelector('cart-drawer-items.is-empty') || doc.querySelector('.drawer__inner-empty');
        cartDrawer.classList.toggle('is-empty', !!isEmpty);
        const iconRes = await fetch(`${cartUrl}?section_id=cart-icon-bubble&_=${ts}`, { cache: 'no-store' });
        if (iconRes.ok) {
          const iconHtml = await iconRes.text();
          const iconDoc = new DOMParser().parseFromString(iconHtml, 'text/html');
          const newIcon = iconDoc.querySelector('#cart-icon-bubble') || iconDoc.querySelector('.shopify-section');
          const curIcon = document.getElementById('cart-icon-bubble');
          if (newIcon && curIcon) curIcon.innerHTML = newIcon.innerHTML;
        }
        return true;
      } catch (e) {
        return false;
      }
    }

    async checkout() {
      if (this._processing || this.state.items.length === 0) return;
      this._processing = true;

      const btn = document.getElementById('bundleCheckoutButton');
      const btnOriginalText = btn ? btn.textContent : '';
      const addingText = this.texts.adding || 'Adding…';
      if (btn) {
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.textContent = addingText;
      }

      const cartDrawer = document.querySelector('cart-drawer');
      const sectionsUrl = window.location.pathname || '/';
      const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' } };
      config.headers = { ...config.headers, 'X-Requested-With': 'XMLHttpRequest' };

      try {
        if (!this.bundleId) {
          this.bundleId = this.generateBundleId();
          this.saveToStorage();
        }

        const cartUrl = (typeof window.routes !== 'undefined' && window.routes.cart_url) ? window.routes.cart_url : '/cart';
        const cartRes = await fetch(cartUrl + '.js', { cache: 'no-store' });
        const cart = await cartRes.json().catch(() => ({}));
        const cartItems = cart.items || [];

        const bundleId = this.bundleId;
        const bundleMap = {};
        cartItems.forEach((line) => {
          const bid = line.properties && (line.properties._bundle_id || line.properties['_bundle_id']);
          if (bid !== bundleId) return;
          const vid = String(line.variant_id || line.id);
          if (!bundleMap[vid]) bundleMap[vid] = { key: line.key, quantity: 0 };
          bundleMap[vid].quantity += line.quantity;
          bundleMap[vid].key = line.key;
        });

        const desiredByVariant = {};
        this.state.items.forEach((item) => {
          const vid = String(item.variantId);
          desiredByVariant[vid] = (desiredByVariant[vid] || 0) + item.quantity;
        });

        const updates = {};

        Object.keys(bundleMap).forEach((vid) => {
          const current = bundleMap[vid].quantity;
          const desired = desiredByVariant[vid] || 0;
          if (desired !== current) updates[bundleMap[vid].key] = desired;
        });

        const toAdd = this.state.items.filter((item) => {
          const vid = String(item.variantId);
          return !bundleMap[vid];
        });

        let lastData = null;

        if (Object.keys(updates).length > 0) {
          const updatePayload = {
            updates,
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: sectionsUrl
          };
          const updateUrl = (typeof window.routes !== 'undefined' && window.routes.cart_update_url) ? window.routes.cart_update_url : '/cart/update';
          const updateJsonUrl = updateUrl.replace(/\/cart\/update$/, '/cart/update.js');
          const updateRes = await fetch(updateJsonUrl, {
            method: config.method,
            headers: config.headers,
            body: JSON.stringify(updatePayload)
          });
          lastData = await updateRes.json().catch(() => ({}));
          if (!updateRes.ok || (lastData.status && lastData.status !== 200)) {
            throw new Error(lastData.description || 'Could not update cart');
          }
        }

        if (toAdd.length > 0) {
          const discount = this.state.discount;
          const addItems = toAdd.map((item) => ({
            id: parseInt(item.variantId, 10),
            quantity: item.quantity,
            properties: {
              _bundle_item: 'true',
              _bundle_discount: discount + '%',
              _bundle_id: bundleId
            }
          }));
          const addPayload = {
            items: addItems,
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: sectionsUrl
          };
          const addUrl = (typeof window.routes !== 'undefined' && window.routes.cart_add_url) ? window.routes.cart_add_url : '/cart/add';
          const addJsonUrl = addUrl.replace(/\/cart\/add$/, '/cart/add.js');
          const addRes = await fetch(addJsonUrl, {
            method: config.method,
            headers: config.headers,
            body: JSON.stringify(addPayload)
          });
          lastData = await addRes.json().catch(() => ({}));
          if (!addRes.ok || (lastData.status && lastData.status !== 200)) {
            throw new Error(lastData.description || 'Could not add to cart');
          }
        }

        if (!lastData && Object.keys(updates).length === 0 && toAdd.length === 0) {
          lastData = { items: [] };
        }

        this.saveToStorage();

        if (!cartDrawer) {
          window.location.href = '/cart';
          return;
        }

        if (btn) cartDrawer.setActiveElement(btn);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          const firstId = (this.state.items[0] && this.state.items[0].variantId) || null;
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'bundle-heights',
            productVariantId: firstId ? parseInt(firstId, 10) : null,
            cartData: lastData
          });
        }

        const hasSections = lastData && lastData.sections && lastData.sections['cart-drawer'];
        if (hasSections) {
          cartDrawer.classList.remove('is-empty');
          const parsedState = {
            ...lastData,
            id: (lastData.items && lastData.items[0] && (lastData.items[0].id || lastData.items[0].variant_id)) || (this.state.items[0] && parseInt(this.state.items[0].variantId, 10))
          };
          cartDrawer.renderContents(parsedState);
        } else {
          this.showCartDrawerLoading(cartDrawer);
          await new Promise((r) => setTimeout(r, 400));
          const ok = await this.refreshCartDrawerAndOpen(cartDrawer);
          this.hideCartDrawerLoading();
          if (ok) {
            cartDrawer.classList.remove('is-empty');
            cartDrawer.open();
          } else {
            window.location.href = '/cart';
          }
        }
      } catch (err) {
        console.error('Bundle checkout error:', err);
        this.hideCartDrawerLoading();
        const errorText = this.texts.errorTryAgain || 'Error — try again';
        const addingText = this.texts.adding || 'Adding…';
        if (btn) {
          btn.textContent = errorText;
          btn.disabled = false;
          btn.setAttribute('aria-disabled', 'false');
          setTimeout(() => {
            btn.textContent = btnOriginalText;
            this.updateCheckoutButton();
          }, 3000);
        }
      } finally {
        this._processing = false;
        const addingText = this.texts.adding || 'Adding…';
        if (btn && btn.textContent === addingText) {
          btn.textContent = btnOriginalText;
          this.updateCheckoutButton();
        }
      }
    }
  }

  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => new BundleHeightsBuilder());
    } else {
      new BundleHeightsBuilder();
    }
  }

  init();
})();
